"use client";

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import type { CreatorProfileForMyLink } from "@artha/types";
import type {
  JobFiltersResponseDto,
  JobsResponseDto,
} from "@artha/api-contracts";
import type { JobFiltersState } from "@artha/ui";
import { defaultJobFeedParams, fetchPublicJobs } from "@/modules/common/services/jobApi";
import { normalizeNicheValue } from "@artha/utils";
import { useJobFiltersState } from "./useJobFiltersState";

const JOBS_PER_PAGE = 10;
const PUBLIC_PROFILE_DEDUP_BY = "content_hash";

type UsePublicProfileJobsParams = {
  initialJobs: JobsResponseDto;
  username: string;
  creator: CreatorProfileForMyLink;
  filters?: JobFiltersResponseDto | null;
};

export const usePublicProfileJobs = ({
  initialJobs,
  username,
  creator,
  filters,
}: UsePublicProfileJobsParams) => {
  const [currentPage, setCurrentPage] = useState(1);
  const preferenceNicheCategories = creator.settings?.nicheCategories ?? [];
  const combinedNicheFallback = useMemo(
    () =>
      Array.from(
        new Set(
          [...(creator.nicheTags ?? []), ...preferenceNicheCategories]
            .map((tag) => tag.trim())
            .filter(Boolean)
            .map(normalizeNicheValue)
        )
      ),
    [creator.nicheTags, preferenceNicheCategories]
  );

  const {
    appliedFilters,
    setAppliedFilters,
    draftFilters,
    setDraftFilters,
    isFiltersOpen,
    setIsFiltersOpen,
    filtersForSheet,
    handleFiltersReset,
    requestParams,
    requestParamsKey,
    preferenceFilterState,
  } = useJobFiltersState({
    creator,
    filters,
    nicheFallback: combinedNicheFallback,
    isPublicProfile: true, // Enable public profile parameter building
  });

  const finalRequestParams = useMemo(
    () => ({
      ...defaultJobFeedParams,
      limit: JOBS_PER_PAGE,
      ...requestParams,
      dedup_by: PUBLIC_PROFILE_DEDUP_BY,
    }),
    [requestParams]
  );
  const requestParamsForApi = useMemo(() => {
    const { categories, ...rest } = finalRequestParams;
    const categoriesValue = Array.isArray(categories) ? categories.join(",") : categories;
    return {
      ...rest,
      ...(categoriesValue ? { categories: categoriesValue } : {}),
    };
  }, [finalRequestParams]);

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedFilters]);

  const handleReset = (next?: JobFiltersState) => {
    handleFiltersReset(next);
    setCurrentPage(1);
  };

  const _hasActiveFilters = useMemo(() => {
    if (appliedFilters.categories.length > 0) return true;
    if (appliedFilters.jobTypes.length > 0) return true;
    if (appliedFilters.experienceLevels.length > 0) return true;
    if (appliedFilters.educationLevels.length > 0) return true;
    if (appliedFilters.workModes.length > 0) return true;
    if (appliedFilters.country) return true;
    if (appliedFilters.state) return true;
    if (appliedFilters.city) return true;
    if (appliedFilters.salaryRanges.length > 0) return true;
    if (appliedFilters.companies.length > 0) return true;
    if (appliedFilters.industries.length > 0) return true;
    return false;
  }, [appliedFilters]);

  const initialData = useMemo(() => {
    if (currentPage !== 1) return undefined;
    if (_hasActiveFilters) return undefined;
    return initialJobs;
  }, [currentPage, _hasActiveFilters, initialJobs]);

  const {
    data,
    isLoading,
    isError,
    error,
    isFetching,
  } = useQuery<JobsResponseDto, Error>({
    queryKey: ["publicJobs", username, requestParamsKey, currentPage],
    queryFn: () =>
      fetchPublicJobs(
        username,
        {
          ...requestParamsForApi,
          offset: (currentPage - 1) * JOBS_PER_PAGE,
        },
        {
          baseUrl: "/api/job-api",
          geoBoost: !appliedFilters.country,
        }
      ),
    initialData,
    placeholderData: () => (currentPage === 1 ? initialData : undefined),
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
    enabled: true,
  });

  const jobs = useMemo(() => data?.items ?? [], [data]);

  // Derive total from recommend API response (no separate count API needed)
  const initialTotalJobs = initialJobs?.total ?? 0;
  const rawTotalJobs = data?.total ?? (currentPage === 1 ? initialTotalJobs : 0);
  const totalJobs =
    initialTotalJobs > 0 && rawTotalJobs > initialTotalJobs
      ? initialTotalJobs
      : rawTotalJobs;
  const totalPages = totalJobs ? Math.ceil(totalJobs / JOBS_PER_PAGE) : 0;

  return {
    jobs,
    totalJobs,
    totalPages,
    isLoading,
    isFetching,
    isError,
    error,
    currentPage,
    setCurrentPage,
    isFiltersOpen,
    setIsFiltersOpen,
    appliedFilters,
    setAppliedFilters,
    draftFilters,
    setDraftFilters,
    filtersForSheet,
    handleFiltersReset: handleReset,
    preferenceFilterState,
  };
};
